import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

// ==========================================
// Types
// ==========================================
export interface GrammarError {
  id: string;
  offset: number;
  length: number;
  from: number;
  to: number;
  message: string;
  shortMessage: string;
  replacements: string[];
  rule: {
    id: string;
    category: string;
    issueType: string;
  };
  dismissed: boolean;
}

export interface GrammarCheckState {
  errors: GrammarError[];
  decorations: DecorationSet;
  isChecking: boolean;
}

// ==========================================
// Plugin Key (exported so hooks can access it)
// ==========================================
export const grammarCheckPluginKey = new PluginKey<GrammarCheckState>('grammarCheck');

// ==========================================
// Helper: Extract plain text with position mapping
// ==========================================
// Returns { text, posMap } where posMap[charIndex] = prosemirror position.
// LanguageTool returns character offsets in plain text, but ProseMirror
// decorations need absolute document positions.
export function extractTextWithPositions(doc: any): { text: string; posMap: number[] } {
  const textChars: string[] = [];
  const posMap: number[] = [];
  let charIndex = 0;

  doc.descendants((node: any, pos: number) => {
    if (node.isText) {
      const nodeText = node.text || '';
      for (let i = 0; i < nodeText.length; i++) {
        textChars.push(nodeText[i]);
        posMap.push(pos + i);
        charIndex++;
      }
    } else if (node.isBlock && charIndex > 0) {
      // Add newline between blocks for proper offset tracking
      textChars.push('\n');
      posMap.push(pos);
      charIndex++;
    }
    return true;
  });

  return { text: textChars.join(''), posMap };
}

// ==========================================
// Helper: Simple hash for cache comparison
// ==========================================
export function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0;
  }
  return hash.toString(36);
}

// ==========================================
// Build decorations from errors
// ==========================================
function buildDecorations(errors: GrammarError[], doc: any, errorClass: string): DecorationSet {
  const decorations = errors
    .filter(e => !e.dismissed && e.from >= 0 && e.to <= doc.content.size && e.from < e.to)
    .map(error =>
      Decoration.inline(error.from, error.to, {
        class: `${errorClass} grammar-${error.rule.issueType}`,
        'data-grammar-error-id': error.id,
      })
    );
  return DecorationSet.create(doc, decorations);
}

// ==========================================
// TipTap Extension
// ==========================================
export interface GrammarCheckOptions {
  errorClass: string;
  debounceMs: number;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    grammarCheck: {
      setGrammarErrors: (errors: GrammarError[]) => ReturnType;
      clearGrammarErrors: () => ReturnType;
      dismissGrammarError: (errorId: string) => ReturnType;
      applyGrammarSuggestion: (errorId: string, replacement: string) => ReturnType;
      setGrammarChecking: (isChecking: boolean) => ReturnType;
    };
  }
}

export const GrammarCheck = Extension.create<GrammarCheckOptions>({
  name: 'grammarCheck',

  addOptions() {
    return {
      errorClass: 'grammar-error',
      debounceMs: 1200,
    };
  },

  addCommands() {
    return {
      setGrammarErrors:
        (errors: GrammarError[]) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(grammarCheckPluginKey, { type: 'SET_ERRORS', errors });
          }
          return true;
        },

      clearGrammarErrors:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(grammarCheckPluginKey, { type: 'CLEAR_ERRORS' });
          }
          return true;
        },

      dismissGrammarError:
        (errorId: string) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(grammarCheckPluginKey, { type: 'DISMISS_ERROR', errorId });
          }
          return true;
        },

      applyGrammarSuggestion:
        (errorId: string, replacement: string) =>
        ({ tr, state, dispatch }) => {
          const pluginState = grammarCheckPluginKey.getState(state);
          if (!pluginState) return false;

          const error = pluginState.errors.find(e => e.id === errorId);
          if (!error) return false;

          if (dispatch) {
            tr.replaceWith(error.from, error.to, state.schema.text(replacement));
            tr.setMeta(grammarCheckPluginKey, { type: 'DISMISS_ERROR', errorId });
          }
          return true;
        },

      setGrammarChecking:
        (isChecking: boolean) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.setMeta(grammarCheckPluginKey, { type: 'SET_CHECKING', isChecking });
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const extensionOptions = this.options;

    return [
      new Plugin<GrammarCheckState>({
        key: grammarCheckPluginKey,

        state: {
          init(): GrammarCheckState {
            return {
              errors: [],
              decorations: DecorationSet.empty,
              isChecking: false,
            };
          },

          apply(tr, prevState, _oldState, newState): GrammarCheckState {
            const meta = tr.getMeta(grammarCheckPluginKey);

            if (meta) {
              switch (meta.type) {
                case 'SET_ERRORS': {
                  const errors: GrammarError[] = meta.errors.filter((e: GrammarError) => !e.dismissed);
                  const decorations = buildDecorations(errors, newState.doc, extensionOptions.errorClass);
                  return { ...prevState, errors, decorations, isChecking: false };
                }

                case 'CLEAR_ERRORS':
                  return { errors: [], decorations: DecorationSet.empty, isChecking: false };

                case 'DISMISS_ERROR': {
                  const errors = prevState.errors.filter(e => e.id !== meta.errorId);
                  const decorations = buildDecorations(errors, newState.doc, extensionOptions.errorClass);
                  return { ...prevState, errors, decorations };
                }

                case 'SET_CHECKING':
                  return { ...prevState, isChecking: meta.isChecking };
              }
            }

            // If doc changed, map existing decorations through the mapping
            if (tr.docChanged && prevState.decorations !== DecorationSet.empty) {
              const mappedDecorations = prevState.decorations.map(tr.mapping, newState.doc);
              const mappedErrors = prevState.errors.map(error => ({
                ...error,
                from: tr.mapping.map(error.from),
                to: tr.mapping.map(error.to),
              }));
              return { ...prevState, errors: mappedErrors, decorations: mappedDecorations };
            }

            return prevState;
          },
        },

        props: {
          decorations(state) {
            return grammarCheckPluginKey.getState(state)?.decorations || DecorationSet.empty;
          },
        },
      }),
    ];
  },
});

export default GrammarCheck;
